import { useState } from 'react';
import { usePeriodStore } from '@/lib/periodStore';
import { cn } from '@/lib/cn';

interface PeriodSelectorProps {
  className?: string;
}

export function PeriodSelector({ className }: PeriodSelectorProps) {
  const { monthIds, range, lastN, allMonthIds, selectLastN, selectAll, selectMonth } =
    usePeriodStore();
  const [isOpen, setIsOpen] = useState(false);

  const totalMonths = allMonthIds.length;
  const currentCount = monthIds.length;

  if (totalMonths === 0) return null;

  const label =
    range === 'all'
      ? `Todos (${totalMonths})`
      : range === 'lastN'
        ? `Últimos ${lastN}`
        : currentCount > 1
          ? `${currentCount} meses`
          : 'Último mes';

  return (
    <div className={cn('relative', className)}>
      <button
        onClick={() => setIsOpen((v) => !v)}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-xl cursor-pointer',
          'text-primary font-bold font-label-md text-label-md',
          'bg-surface-container-lowest',
          'shadow-[3px_3px_6px_0_rgba(140,113,102,0.18),-3px_-3px_6px_0_rgba(255,255,255,0.85)]',
          'transition-all duration-200',
          'hover:shadow-[4px_4px_8px_0_rgba(140,113,102,0.22),-4px_-4px_8px_0_rgba(255,255,255,0.92)]',
          isOpen &&
            'shadow-[inset_3px_3px_6px_0_rgba(140,113,102,0.20),inset_-3px_-3px_6px_0_rgba(255,255,255,0.85)]',
        )}
      >
        <span className="material-symbols-outlined text-lg">date_range</span>
        <span>{label}</span>
        <span className="material-symbols-outlined text-lg">
          {isOpen ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-3 right-0 min-w-[280px] neu-card p-2 z-50">
          <div className="text-label-sm text-on-surface-variant opacity-70 px-3 py-2 uppercase tracking-wider">
            Período
          </div>
          <button
            onClick={() => {
              if (allMonthIds[0]) selectMonth(allMonthIds[0]);
              setIsOpen(false);
            }}
            className={cn(
              'w-full text-left px-4 py-2.5 rounded-xl cursor-pointer',
              'hover:bg-primary/5 transition-all',
              range === 'single' && currentCount === 1 && 'bg-primary/10 text-primary font-bold',
            )}
          >
            <div className="font-bold">Último mes</div>
            <div className="text-label-sm text-on-surface-variant opacity-70">
              Solo el más reciente
            </div>
          </button>
          <div className="h-px neu-divider my-1" />
          <div className="text-label-sm text-on-surface-variant opacity-70 px-3 py-1.5">
            Últimos N meses
          </div>
          <div className="grid grid-cols-4 gap-1 px-2 pb-2">
            {Array.from({ length: Math.min(12, totalMonths) }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => {
                  selectLastN(n);
                  setIsOpen(false);
                }}
                className={cn(
                  'py-2 rounded-lg text-label-sm font-bold cursor-pointer',
                  'bg-surface-container',
                  'shadow-[2px_2px_4px_0_rgba(140,113,102,0.15),-2px_-2px_4px_0_rgba(255,255,255,0.85)]',
                  'transition-all duration-200',
                  'hover:shadow-[inset_2px_2px_4px_0_rgba(140,113,102,0.18),inset_-2px_-2px_4px_0_rgba(255,255,255,0.80)]',
                  lastN === n && range === 'lastN' && 'bg-primary text-on-primary',
                )}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="h-px neu-divider my-1" />
          <button
            onClick={() => {
              selectAll();
              setIsOpen(false);
            }}
            className={cn(
              'w-full text-left px-4 py-2.5 rounded-xl cursor-pointer',
              'hover:bg-primary/5 transition-all',
              range === 'all' && 'bg-primary/10 text-primary font-bold',
            )}
          >
            <div className="font-bold flex items-center gap-2">
              <span className="material-symbols-outlined text-lg">all_inclusive</span>
              Todos ({totalMonths})
            </div>
            <div className="text-label-sm text-on-surface-variant opacity-70">
              Vista acumulada completa
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
